import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORY_MAP, fmtNumber } from "@/lib/constants";

function Field({ label, children, hint, required }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-600">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

const inputCls =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-base text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function SectionTitle({ children }) {
  return (
    <h4 className="mt-2 border-b border-slate-100 pb-2 text-sm font-bold uppercase tracking-wider text-slate-500">
      {children}
    </h4>
  );
}

const EMPTY = {
  name: "", supplier: "", use_area: "", art_number: "", composition: "",
  construction: "", fabric_type: "", width: "", weight: "", size: "",
  tex_number: "", ticket_number: "", delivery_date: "", garments_quantity: "",
  consumption: 0, consumption_unit: "", required_qty: 0, approval_status: "pending",
  lead_time_days: 0, to_be_booked_on: "", actual_booking_date: "", etd: "",
  eta: "", to_be_in_house: "", actual_in_house_date: "", remarks: "", approval_date: "",
};

export function MaterialModal({ open, onClose, onSave, category, orderQty, item, orderLabel, readOnly }) {
  const cat = CATEGORY_MAP[category] || {};
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, consumption_unit: cat.unit || "", ...(item || {}) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, category]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const qtyBase = category === "buttons" ? (Number(form.garments_quantity) || 0) : (Number(orderQty) || 0);
  let autoQty;
  if (category === "threads") {
    const lpc = Number(form.width) || 0;
    autoQty = lpc ? Math.round(((Number(form.consumption) || 0) * (Number(form.garments_quantity) || 0)) / lpc) : 0;
  } else {
    autoQty = Math.round((Number(form.consumption) || 0) * qtyBase);
  }

  const submit = () => {
    const payload = {
      ...form,
      category,
      consumption: Number(form.consumption) || 0,
      required_qty: Number(form.required_qty) || autoQty,
      lead_time_days: Number(form.lead_time_days) || 0,
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto mf-scrollbar p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-4">
          <DialogTitle className="font-heading text-2xl font-bold text-slate-900">
            {item ? `Edit ${cat.singular}` : cat.addLabel}
          </DialogTitle>
          {orderLabel && <p className="text-sm text-slate-500">{orderLabel}</p>}
        </DialogHeader>

        <fieldset disabled={readOnly} className="space-y-5 px-6 py-5">
          <SectionTitle>Basic Details</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={cat.showFabricType ? "Fabric label" : category === "threads" ? "Item / Brand Name" : "Item name"} required>
              <input data-testid="material-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Fabric A - shell fabric" />
            </Field>
            <Field label="Supplier name">
              <input data-testid="material-supplier-input" className={inputCls} value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Supplier" />
            </Field>
            <Field label="Approval Date">
              <input data-testid="material-approval-date-input" type="date" className={inputCls} value={(form.approval_date || "").slice(0, 10)} onChange={(e) => set("approval_date", e.target.value)} />
            </Field>
            {cat.showFabricType && (
              <Field label="Type of fabric">
                <input className={inputCls} value={form.fabric_type} onChange={(e) => set("fabric_type", e.target.value)} placeholder="e.g. Knitted fabric" />
              </Field>
            )}
            {cat.showFabricType && (
              <Field label="Fabric composition">
                <input className={inputCls} value={form.composition} onChange={(e) => set("composition", e.target.value)} placeholder="e.g. 100% Cotton" />
              </Field>
            )}
            <Field label="Use area">
              <input className={inputCls} value={form.use_area} onChange={(e) => set("use_area", e.target.value)} placeholder="e.g. Body / Collar / Placket" />
            </Field>
            <Field label={category === "threads" ? "Code Number" : "Art / Reference number"}>
              <input className={inputCls} value={form.art_number} onChange={(e) => set("art_number", e.target.value)} placeholder="Art no." />
            </Field>
            {cat.showSize && (
              <Field label="Size">
                <input className={inputCls} value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="e.g. 18L" />
              </Field>
            )}
            {!cat.showFabricType && (
              <Field label={category === "threads" ? "Length per Cone" : "Width"}>
                <input className={inputCls} value={form.width} onChange={(e) => set("width", e.target.value)} placeholder="e.g. 60 inch" />
              </Field>
            )}
            {category === "threads" && (
              <>
                <Field label="Tex Number">
                  <input data-testid="material-tex-input" className={inputCls} value={form.tex_number} onChange={(e) => set("tex_number", e.target.value)} placeholder="e.g. Tex 40" />
                </Field>
                <Field label="Ticket Number">
                  <input data-testid="material-ticket-input" className={inputCls} value={form.ticket_number} onChange={(e) => set("ticket_number", e.target.value)} placeholder="e.g. 120" />
                </Field>
                <Field label="Garments quantity">
                  <input data-testid="material-thread-garments-qty-input" type="number" className={inputCls} value={form.garments_quantity} onChange={(e) => set("garments_quantity", e.target.value)} placeholder="e.g. 5000" />
                </Field>
              </>
            )}
            {category === "interlining" && (
              <Field label="Weight">
                <input data-testid="material-weight-input" className={inputCls} value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 50 GSM" />
              </Field>
            )}
            {cat.showFabricType && (
              <>
                <Field label="Width">
                  <input data-testid="material-width-input" className={inputCls} value={form.width} onChange={(e) => set("width", e.target.value)} placeholder="e.g. 60 inch" />
                </Field>
                <Field label="Construction">
                  <input data-testid="material-construction-input" className={inputCls} value={form.construction} onChange={(e) => set("construction", e.target.value)} placeholder="e.g. Single Jersey, 20s" />
                </Field>
                <Field label="Fabric Weight">
                  <input data-testid="material-weight-input" className={inputCls} value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 180 GSM" />
                </Field>
              </>
            )}
            {category === "buttons" && (
              <Field label="Garments Quantity">
                <input data-testid="material-garments-qty-input" type="number" className={inputCls} value={form.garments_quantity} onChange={(e) => set("garments_quantity", e.target.value)} placeholder="e.g. 5000" />
              </Field>
            )}
          </div>

          <SectionTitle>Quantity Calculation</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Consumption per piece" required>
              <input data-testid="material-consumption-input" type="number" step="0.01" className={inputCls} value={form.consumption} onChange={(e) => set("consumption", e.target.value)} />
            </Field>
            <Field label="Unit">
              <input className={inputCls} value={form.consumption_unit} onChange={(e) => set("consumption_unit", e.target.value)} placeholder={cat.unit} />
            </Field>
            <Field label={category === "buttons" ? "Required quantity with loss" : "Required quantity"} hint={category === "threads" ? `Auto: (${fmtNumber(form.consumption)} × ${fmtNumber(form.garments_quantity)}) / ${fmtNumber(form.width)} = ${fmtNumber(autoQty)}` : `Auto: ${fmtNumber(form.consumption)} × ${fmtNumber(qtyBase)} = ${fmtNumber(autoQty)}`}>
              <input data-testid="material-reqqty-input" type="number" className={inputCls} value={form.required_qty || autoQty} onChange={(e) => set("required_qty", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Approval status">
              <Select value={form.approval_status} onValueChange={(v) => set("approval_status", v)}>
                <SelectTrigger data-testid="material-approval-select" className="h-11 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="received">In-housed</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Lead time (days)">
              <input type="number" className={inputCls} value={form.lead_time_days} onChange={(e) => set("lead_time_days", e.target.value)} />
            </Field>
          </div>

          <SectionTitle>Timeline & Booking</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="To be booked on">
              <input data-testid="material-bookdate-input" type="date" className={inputCls} value={(form.to_be_booked_on || "").slice(0, 10)} onChange={(e) => set("to_be_booked_on", e.target.value)} />
            </Field>
            <Field label="Actual booking date">
              <input type="date" className={inputCls} value={(form.actual_booking_date || "").slice(0, 10)} onChange={(e) => set("actual_booking_date", e.target.value)} />
            </Field>
            <Field label="ETD (estimated departure)">
              <input type="date" className={inputCls} value={(form.etd || "").slice(0, 10)} onChange={(e) => set("etd", e.target.value)} />
            </Field>
            <Field label="ETA (estimated arrival)">
              <input type="date" className={inputCls} value={(form.eta || "").slice(0, 10)} onChange={(e) => set("eta", e.target.value)} />
            </Field>
            <Field label="To be in-house by">
              <input type="date" className={inputCls} value={(form.to_be_in_house || "").slice(0, 10)} onChange={(e) => set("to_be_in_house", e.target.value)} />
            </Field>
            <Field label="Actual in-house / arrival date">
              <input type="date" className={inputCls} value={(form.actual_in_house_date || "").slice(0, 10)} onChange={(e) => set("actual_in_house_date", e.target.value)} />
            </Field>
          </div>
          <Field label="Remarks">
            <textarea className="min-h-[80px] w-full rounded-lg border border-slate-200 bg-white p-3 text-base text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Optional notes…" />
          </Field>
        </fieldset>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button data-testid="material-cancel-btn" variant="outline" className="h-11 px-6" onClick={onClose}>
            Cancel
          </Button>
          {!readOnly && (
            <Button data-testid="material-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={submit}>
              {item ? "Save changes" : cat.addLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
